import {
    DateTime,
    IANAZone,
} from 'luxon'
import {useTimeFormat} from '../../hooks/datetime/datetime'
import {useTranslation} from 'react-i18next'

type IntlWithSupportedValuesOf = typeof Intl & {
    supportedValuesOf?: (key: 'timeZone') => Array<string>
}

const getSupportedTimezones = () => {
    const {supportedValuesOf} = (Intl ?? {}) as IntlWithSupportedValuesOf
    return typeof supportedValuesOf === 'function'
        ? supportedValuesOf('timeZone')
        : undefined
}

const supportedTimezones = getSupportedTimezones()

export const isCurrentYear = (date: Date) => date.getUTCFullYear() === new Date().getUTCFullYear()

export const isTimezone = (value?: string | null) => {
    if (!value) {
        return false
    }

    return supportedTimezones
        ? supportedTimezones.indexOf(value) !== -1
        : IANAZone.isValidZone(value)
}

export const isToday = (date: Date): boolean => {
    const today = new Date().setHours(0, 0, 0, 0)
    const thatDay = new Date(date).setHours(0, 0, 0, 0)

    return today === thatDay
}

export const isSameDay = (time1?: number, time2?: number): boolean => {
    const d1 = DateTime.fromSeconds(time1 ?? 0)
    const d2 = DateTime.fromSeconds(time2 ?? 0)
    return d1.hasSame(d2, 'day')
}

export const isTomorrow = (date: Date): boolean => {
    const tomorrow = new Date(new Date().getTime() + 8.64e7).setHours(0, 0, 0, 0)
    const thatDay = new Date(date).setHours(0, 0, 0, 0)
    return tomorrow === thatDay
}

export const isPast = (date: Date): boolean => {
    const today = new Date().setHours(0, 0, 0, 0)
    const thatDay = new Date(date).setHours(0, 0, 0, 0)

    return thatDay < today
}

export const date = (t: number): string => {
    const [hour, currentLocale] = useTimeFormat()
    const d = new Date(t * 1000)

    return new Intl.DateTimeFormat(currentLocale, {
        ...hour && {hour12: hour === '12'},
        dateStyle: 'short',
    }).format(d)
}

export const longDatetime = (t: number): string => {
    const [hour, currentLocale] = useTimeFormat()
    const d = new Date(t * 1000)

    return new Intl.DateTimeFormat(currentLocale, {
        ...hour && {hour12: hour === '12'},
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        month: 'long',
        second: 'numeric',
        weekday: 'long',
        year: 'numeric',
    }).format(d)
}

export const shortDate = (t: number): string => {
    const [hour, currentLocale] = useTimeFormat()
    const d = new Date(t * 1000)

    return new Intl.DateTimeFormat(currentLocale, {
        ...hour && {hour12: hour === '12'},
        day: 'numeric',
        month: 'numeric',
    }).format(d)
}

const padToTwoDigits = (num: number) => num.toString().padStart(2, '0')

export const minutesToHoursAndMinutes = (totalMinutes: number) => {
    const hours = Math.floor(totalMinutes / 60)
    const minutes = totalMinutes % 60

    return `${hours}:${padToTwoDigits(minutes)}`
}

export const hoursAndMinutesToMinutes = (time: string): number => {
    const parts = time.split(':')

    const hours = Number(parts[0])
    const minutes = Number(parts[1])

    // eslint-disable-next-line no-extra-parens
    return (hours * 60) + minutes
}

export const datetime = (t: number): string => {
    const [hour, currentLocale] = useTimeFormat()
    const d = new Date(t * 1000)

    if (isToday(d)) {
        return new Intl.DateTimeFormat(currentLocale, {
            ...hour && {hour12: hour === '12'},
            hour: 'numeric',
            minute: 'numeric',
        }).format(d)
    }

    if (isCurrentYear(d)) {
        return new Intl.DateTimeFormat(currentLocale, {
            ...hour && {hour12: hour === '12'},
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            month: 'numeric',
        }).format(d)
    }

    return new Intl.DateTimeFormat(currentLocale, {
        ...hour && {hour12: hour === '12'},
        dateStyle: 'short',
        timeStyle: 'short',
    }).format(d)
}

export const time = (t: number): string => {
    const [hour, currentLocale] = useTimeFormat()
    const d = new Date(t * 1000)

    return new Intl.DateTimeFormat(currentLocale, {
        ...hour && {hour12: hour === '12'},
        hour: 'numeric',
        minute: 'numeric',
    }).format(d)
}

export const getToday = (): number => new Date().setHours(0, 0, 0, 0)
export const getYesterday = (): number => {
    const today = new Date(getToday())
    today.setDate(today.getDate() - 1)
    return today.setHours(0, 0, 0, 0)
}

export type DateRange = 'today' | 'tomorrow' | 'past' | 'future'
export const asDateString = (currentDate = '0'): {
    date: string;
    kind: DateRange;
} => {
    const {i18n} = useTranslation()
    const currentLocale = i18n.language
    const asDate = new Date(parseInt(currentDate, 10))
    const dateString = new Intl.DateTimeFormat(currentLocale, {
        day: '2-digit',
        month: '2-digit',
        weekday: 'long',
    }).format(asDate)
        .replace(/\//ug, '.')
        .replace(/,/ug, '')
    if (isPast(asDate)) {
        return {
            date: dateString,
            kind: 'past',
        }
    } else if (isToday(asDate)) {
        return {
            date: dateString,
            kind: 'today',
        }
    } else if (isTomorrow(asDate)) {
        return {
            date: dateString,
            kind: 'tomorrow',
        }
    }

    return {
        date: dateString,
        kind: 'future',
    }
}

export const toLocalTime = (dateString = ''): Date => {
    // has timezone info
    const hasTimezoneInfo = dateString.includes('Z')
    if (hasTimezoneInfo) {
        return new Date(dateString)
    }
    // add timezone info ( UTC +0000 )
    const dateObj = new Date(`${dateString}Z`)
    if (!isNaN(dateObj.getTime())) {
        return dateObj
    }
    return new Date('1971-01-01T00:00:00Z')
}

export const weekdaysForLocale = (weekday: ('long' | 'short') = 'long') => {
    const {i18n} = useTranslation()
    const currentLocale = i18n.language

    const {format} = new Intl.DateTimeFormat(currentLocale, {weekday})
    return [...Array(7).keys()]
        .map((day) => format(new Date(Date.UTC(2021, 5, day))))
}
